import React, { useEffect, useState } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';
import { useTranslations } from 'next-intl';
import Banner from '@/components/commons/Banner';
import AddCustomerService from '@/components/commons/AddCustomerService';
import { ImmgrationProps } from '@/components/immigration/MigrantContent';
import { scrollToElement } from '@/shared/utils/tools';
import { useRouter } from 'next/router';
import Head from '@/components/commons/Head';
import Masonry from 'react-masonry-css';

// 留学列表数据 -- 数据只有两级
export const studyAbroadTree: ImmgrationProps[] = [
  {
    value: '加拿大学校申请',
    label: '加拿大学校申请',
    enLabel: 'Apply for Schools',
    children: [
      {
        value: '中小学公校申请',
        label: '中小学公校申请',
        enLabel: 'Public School Application for Primary and Secondary Education',
        link: '/school searching tool/elementory school and secondary school application/',
      },
      {
        value: '中小学私校申请',
        label: '中小学私校申请',
        enLabel: 'Private School Application for Primary and Secondary Education',
        link: '/school searching tool/private school application/',
      },
      {
        value: '本硕博申请',
        label: '本硕博申请',
        enLabel: 'Undergraduate/Graduate School Application',
        link: '/加拿大本科、硕士与博士申请完整指南/',
      },
      {
        value: '大专/职业院校申请',
        label: '大专/职业院校申请',
        enLabel: 'College and Vocational School Application',
        link: '/加拿大大专（college）留学申请详解/',
      },
    ],
  },
  {
    value: '加拿大学签申请',
    label: '加拿大学签申请',
    enLabel: 'Apply for Study Permits',
    children: [
      {
        value: '学签申请',
        label: '学签申请',
        enLabel: 'Apply for Study Permits',
        link: '/学签申请/',
      },
      {
        value: '学签续签',
        label: '学签续签',
        enLabel: 'Study Permits Extension',
        link: '/2025年加拿大学签续签全攻略/',
      },
      {
        value: '转学办理',
        label: '转学办理',
        enLabel: 'Transfer between Schools',
        link: '/加拿大留学生转学攻略（2025版）/',
      },
      {
        value: '留学生配偶工签',
        label: '留学生配偶工签',
        enLabel: 'Spouse Work Permit',
        link: '/visa/student%20related%20work%20permit/student%20spouse%20work%20permit/',
      },
    ],
  },
  {
    value: '其它国家留学',
    label: '其它国家留学',
    enLabel: 'Studying in other countries',
    children: [
      {
        value: '藤校规划与申请',
        label: '藤校及海外名校规划与申请',
        enLabel: 'Ivy League Application',
        link: '/美国藤校与海外名校申请指南/',
      },
    ],
  },
];

const StudyAbroadContent = () => {
  const t = useTranslations();
  const { query } = useRouter();
  const { keyword } = query; // url连接上带的搜索关键词

  const [searchSelectedItem, setSearchSelectedItem] = useState<ImmgrationProps | null>(null); //选中的搜索项
  const [searchValue, setSearchValue] = useState<string>('');

  const findLeafNodeAndSetActive = (tree: ImmgrationProps[], query: string): void => {
    // 内部递归查找方法
    const findNode = (nodes: ImmgrationProps[], target: string, path: string[] = []): any => {
      // 将查询关键字转换为小写，确保大小写不敏感
      const lowerCaseTarget = target.toLowerCase();

      for (const node of nodes) {
        const newPath = [...path, node.value];

        // 将节点的值和标签转换为小写，进行不区分大小写的比较
        const lowerCaseValue = node.value.toLowerCase();
        const lowerCaseLabel = node.label.toLowerCase();
        const lowerCaseEnLabel = node.enLabel ? node.enLabel.toLowerCase() : '';

        // 检查是否与目标模糊匹配
        if (
          lowerCaseValue.includes(lowerCaseTarget) ||
          lowerCaseLabel.includes(lowerCaseTarget) ||
          lowerCaseEnLabel.includes(lowerCaseTarget)
        ) {
          // 如果找到叶子节点（无 children 或 children 为空）
          if (!node.children || node.children.length === 0) {
            return { leafNode: node, path: newPath };
          }
        }
        // 递归查找子节点
        if (node.children) {
          const result = findNode(node.children, target, newPath);
          if (result) return result;
        }
      }
      return null;
    };

    // 开始查找
    const result = findNode(tree, query);

    if (result) {
      const { leafNode } = result;

      setSearchSelectedItem(leafNode);
    } else {
      console.warn('未找到匹配的叶子节点');
      setSearchSelectedItem(null);
    }
  };

  useEffect(() => {
    if (keyword) {
      // 如果有搜索关键字，执行搜索方法
      findLeafNodeAndSetActive(studyAbroadTree, keyword as string);
    }
  }, [keyword]);

  return (
    <div className="w-full">
      <Head
        title={'加拿大留学,游学,夏令营,留学签证办理,学校申请'}
        description={
          '星环移民，专注加拿大移民和留学十余载，擅长量身定制方案，提供高性价比服务，成功率居行业领先水平。旗下拥有多位全职的ICCRC认证的「加拿大持牌移民顾问」、教育专家和职业培训导师，专业办理加拿大移民、留学、游学、夏令营、教育等服务，提供顾问咨询、签证办理、学校申请、就业指导、财税规划及新移民安家落地、置业创业等一条龙服务，对于解决各种签证、留学、移民疑难问题有丰富经验与成功案例。'
        }
        keywords="移民,海外移民,加拿大移民,全球移民,移民咨询,移民公司,移民中介,星环移民"
      />
      {/* 留学banner */}
      <Banner
        title={t('StudyAbroad.foreignStudentEducation')}
        subTitlle={t('StudyAbroad.foreignStudentEducationEnlishText')}
        onSearch={(value: string) => {
          setSearchValue(value);
          if (value) {
            findLeafNodeAndSetActive(studyAbroadTree, value);
          }
        }}
        showError={{
          show: !searchSelectedItem && !!searchValue,
          message: t('Bannder.errorText'),
        }}
        onClickConsultCustomerService={() => {
          const element = document.getElementById('addCustomerServiceBox');
          if (element) {
            scrollToElement(element);
          }
        }}
      />
      {/* 留学内容 */}
      <div className="box-border w-full bg-[#E7EEFF] px-[9.36vw] pb-[6.5vw] pt-[1.56vw] max-md:-mt-[138px] max-md:rounded-t-3xl max-md:px-[4%] max-md:pb-[29px] max-md:pt-[18px]">
        <Masonry breakpointCols={2} className="my-masonry-grid max-md:hidden" columnClassName="my-masonry-grid_column">
          {(studyAbroadTree || []).map((item, index) => {
            return (
              <div key={index} className="mb-[2.08vw] w-full rounded-[1.04vw] bg-[#33367C] max-md:mb-6">
                <h2 className="box-border flex h-[4.316vw] flex-row items-center justify-start px-[1.872vw] text-left text-[1.248vw] font-medium text-white max-md:h-8 max-md:px-4 max-md:text-[14px]">
                  {item.label}
                </h2>
                <div className="rounded-[1.04vw] bg-white px-[1.82vw] pb-[2.756vw] pt-[2.34vw] max-md:p-6 max-md:px-4 max-md:py-4">
                  {(item?.children || [])?.length > 0 ? (
                    <div>
                      {(item?.children || []).map((_item, index) => (
                        <div
                          className="mb-[1.976vw] flex cursor-pointer flex-row justify-start text-[0.936vw] font-medium text-[#2A2B45] last:mb-0 hover:font-semibold hover:text-[#19228C] max-md:text-xs"
                          key={index}
                          onClick={() => {
                            if (_item?.link) {
                              const fullLink = _item.link.startsWith('http')
                                ? _item.link
                                : process.env.NEXT_PUBLIC_WEBSITE + _item.link;
                              window.open(fullLink, '_blank');
                            }
                          }}
                        >
                          <span className="mr-[1.04vw] w-2/5">{_item.label}</span>
                          <span className="flex-1">{_item.enLabel}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center max-md:text-xs">{t('Home.noData')}</div>
                  )}
                </div>
              </div>
            );
          })}
        </Masonry>
        <Masonry breakpointCols={1} className="my-masonry-grid md:hidden" columnClassName="my-masonry-grid_column">
          {(studyAbroadTree || []).map((item, index) => {
            return (
              <div key={index} className="mb-[2.08vw] w-full rounded-[1.04vw] bg-[#33367C] max-md:mb-6">
                <h2 className="box-border flex h-[4.316vw] flex-row items-center justify-start px-[1.872vw] text-left text-[1.248vw] font-medium text-white max-md:h-8 max-md:px-4 max-md:text-[14px]">
                  {item.label}
                </h2>
                <div className="rounded-[1.04vw] bg-white px-[1.82vw] pb-[2.756vw] pt-[2.34vw] max-md:p-6 max-md:px-4 max-md:py-4">
                  {(item?.children || [])?.length > 0 ? (
                    <div>
                      {(item?.children || []).map((_item, index) => (
                        <div
                          className="mb-[1.976vw] flex cursor-pointer flex-row justify-start text-[0.936vw] font-medium text-[#2A2B45] last:mb-0 hover:font-semibold hover:text-[#19228C] max-md:text-xs"
                          key={index}
                          onClick={() => {
                            if (_item?.link) {
                              const fullLink = _item.link.startsWith('http')
                                ? _item.link
                                : process.env.NEXT_PUBLIC_WEBSITE + _item.link;
                              window.open(fullLink, '_blank');
                            }
                          }}
                        >
                          <span className="mr-[1.04vw] w-2/5">{_item.label}</span>
                          <span className="flex-1">{_item.enLabel}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center max-md:text-xs">{t('Home.noData')}</div>
                  )}
                </div>
              </div>
            );
          })}
        </Masonry>
      </div>
      {/* 添加客服 */}
      <AddCustomerService />
      {/* 加拿大教育咨询 */}
      <div
        style={{
          backgroundImage: 'url(/images/studyAbroad/bg.png), linear-gradient(180deg, #E7EEFF 0%, #FFFFFF 100%)',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'bottom right',
          backgroundSize: '100% auto',
        }}
        className="w-full pb-[8.944vw] pt-[6.916vw]"
      >
        <div className="box-border w-full px-[9.672vw] max-md:px-[4%]">
          <h1 className="mb-[1.04vw] text-[2.08vw] font-medium leading-[2.08vw] text-[#2A2B45] max-md:mb-[10px] max-md:text-xl max-md:leading-5">
            {t('StudyAbroad.overviewOfEducationInCanada')}
          </h1>
          <h1 className="mb-[3.796vw] text-[1.56vw] font-bold uppercase leading-[1.56vw] text-[#2A2B45] opacity-20 max-md:mb-[18px] max-md:text-lg max-md:leading-[18px]">
            {t('StudyAbroad.overviewOfEducationInCanadaEnlishText')}
          </h1>
        </div>
        <div className="grid w-full grid-cols-1 gap-[2.496vw] px-[11.752vw] max-md:gap-6 max-md:px-[4%] lg:grid-cols-2 2xl:gap-[2.496vw]">
          <div
            style={{
              boxShadow: '-1px 7px 7px 0px rgba(42,42,42,0.17)',
              background: 'linear-gradient(50deg, #383968 0%, #595CAA 71%)',
            }}
            className="flex min-h-[22.88vw] w-full flex-col items-start justify-start rounded-[1.04vw]"
          >
            <h2 className="flex h-[4.316vw] w-full flex-row items-center justify-start px-[1.82vw] text-[1.56vw] font-medium leading-[1.56vw] text-white max-md:h-8 max-md:px-4 max-md:text-[14px]">
              {t('StudyAbroad.overviewOfEducationInCanadaCard1Title')}
            </h2>
            <div className="box-border w-full flex-1 rounded-[1.04vw] bg-white px-[1.82vw] py-[2.6vw] max-md:p-6 max-md:px-4 max-md:py-4">
              <div
                className="mb-[1.976vw] flex w-full cursor-pointer flex-row items-center justify-start last:mb-0"
                onClick={() => {
                  window.open(process.env.NEXT_PUBLIC_WEBSITE + '/overview-of-canada/school-ranking-canada/', '_blank');
                }}
              >
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard1Content1')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard1EnglishContent1')}
                </h3>
              </div>
              <div
                className="mb-[1.976vw] flex w-full cursor-pointer flex-row items-center justify-start last:mb-0"
                onClick={() => {
                  window.open(process.env.NEXT_PUBLIC_WEBSITE + '/大温公立中小学评分分布图/', '_blank');
                }}
              >
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard1Content2')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard1EnglishContent2')}
                </h3>
              </div>
              <div
                className="mb-[1.976vw] flex w-full cursor-pointer flex-row items-center justify-start last:mb-0"
                onClick={() => {
                  window.open(
                    process.env.NEXT_PUBLIC_WEBSITE + '/overview-of-canada/education-in-canada/middle-school/',
                    '_blank',
                  );
                }}
              >
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard1Content3')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard1EnglishContent3')}
                </h3>
              </div>
              <div
                className="flex w-full cursor-pointer flex-row items-center justify-start last:mb-0"
                onClick={() => {
                  window.open(
                    process.env.NEXT_PUBLIC_WEBSITE +
                      '/overview-of-canada/education-in-canada/education-analysis/canada-education-system-private-school-analysis/',
                    '_blank',
                  );
                }}
              >
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard1Content4')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard1EnglishContent4')}
                </h3>
              </div>
            </div>
          </div>
          <div
            style={{
              boxShadow: '-1px 7px 7px 0px rgba(42,42,42,0.17)',
              background: 'linear-gradient(50deg, #383968 0%, #595CAA 71%)',
            }}
            className="flex min-h-[22.88vw] w-full flex-col items-start justify-start rounded-[1.04vw]"
          >
            <h2 className="flex h-[4.316vw] w-full flex-row items-center justify-start px-[1.82vw] text-[1.56vw] font-medium leading-[1.56vw] text-white max-md:h-8 max-md:px-4 max-md:text-[14px]">
              {t('StudyAbroad.overviewOfEducationInCanadaCard2Title')}
            </h2>
            <div className="box-border w-full flex-1 rounded-[1.04vw] bg-white px-[1.82vw] py-[2.6vw] max-md:px-4 max-md:py-4 2xl:px-[35px]">
              <div className="mb-[1.976vw] flex w-full flex-row items-center justify-start last:mb-0">
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard2Content1')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard2EnglishContent1')}
                </h3>
              </div>
              <div className="mb-[1.976vw] flex w-full flex-row items-center justify-start last:mb-0">
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard2Content2')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard2EnglishContent2')}
                </h3>
              </div>
              <div className="flex w-full flex-row items-center justify-start last:mb-0">
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard2Content3')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard2EnglishContent3')}
                </h3>
              </div>
            </div>
          </div>
          <div
            style={{
              boxShadow: '-1px 7px 7px 0px rgba(42,42,42,0.17)',
              background: 'linear-gradient(50deg, #383968 0%, #595CAA 71%)',
            }}
            className="flex min-h-[22.88vw] w-full flex-col items-start justify-start rounded-[1.04vw]"
          >
            <h2 className="flex h-[4.316vw] w-full flex-row items-center justify-start px-[1.82vw] text-[1.56vw] font-medium leading-[1.56vw] text-white max-md:h-8 max-md:px-4 max-md:text-[14px]">
              {t('StudyAbroad.overviewOfEducationInCanadaCard3Title')}
            </h2>
            <div className="box-border w-full flex-1 rounded-[1.04vw] bg-white px-[1.82vw] py-[2.6vw] max-md:px-4 max-md:py-4 2xl:px-[35px]">
              <div className="mb-[1.976vw] flex w-full flex-row items-center justify-start last:mb-0">
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3Content1')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3EnglishContent1')}
                </h3>
              </div>
              <div
                className="mb-[1.976vw] flex w-full cursor-pointer flex-row items-center justify-start last:mb-0"
                onClick={() => {
                  window.open(process.env.NEXT_PUBLIC_WEBSITE + '/overview-of-canada/school-ranking-canada/', '_blank');
                }}
              >
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3Content2')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3EnglishContent2')}
                </h3>
              </div>
              <div className="mb-[1.976vw] flex w-full flex-row items-center justify-start last:mb-0">
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3Content3')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3EnglishContent3')}
                </h3>
              </div>
              <div
                className="mb-[1.976vw] flex w-full cursor-pointer flex-row items-center justify-start last:mb-0"
                onClick={() => {
                  window.open(
                    process.env.NEXT_PUBLIC_WEBSITE + '/安省省提名硕博直通项目oinp-master-and-phd-graduate-stream/',
                    '_blank',
                  );
                }}
              >
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3Content4')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3EnglishContent4')}
                </h3>
              </div>
              <div className="flex w-full flex-row items-center justify-start last:mb-0">
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3Content5')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard3EnglishContent5')}
                </h3>
              </div>
            </div>
          </div>
          <div
            style={{
              boxShadow: '-1px 7px 7px 0px rgba(42,42,42,0.17)',
              background: 'linear-gradient(50deg, #383968 0%, #595CAA 71%)',
            }}
            className="flex min-h-[22.88vw] w-full flex-col items-start justify-start rounded-[1.04vw]"
          >
            <h2 className="flex h-[4.316vw] w-full flex-row items-center justify-start px-[1.82vw] text-[1.56vw] font-medium leading-[1.56vw] text-white max-md:h-8 max-md:px-4 max-md:text-[14px]">
              {t('StudyAbroad.overviewOfEducationInCanadaCard4Title')}
            </h2>
            <div className="box-border w-full flex-1 rounded-[1.04vw] bg-white px-[1.82vw] py-[2.6vw] max-md:px-4 max-md:py-4 2xl:px-[35px]">
              <div
                className="mb-[1.976vw] flex w-full cursor-pointer flex-row items-center justify-start last:mb-0"
                onClick={() => {
                  window.open('https://www.ielts-writing.info/EXAM/general_writing_samples_task_1/', '_blank');
                }}
              >
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard4Content1')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard4EnglishContent1')}
                </h3>
              </div>
              <div className="flex w-full flex-row items-center justify-start last:mb-0">
                <h3 className="mr-[0.936vw] w-2/5 text-[1.04vw] font-medium leading-[1.04vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard4Content2')}
                </h3>
                <h3 className="flex-1 text-[0.936vw] font-normal leading-[0.936vw] text-[#2A2B45] max-md:text-xs">
                  {t('StudyAbroad.overviewOfEducationInCanadaCard4EnglishContent2')}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyAbroadContent;
